import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Search, FileText, Code, TestTube, Rocket, HeadphonesIcon, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const process = [
  {
    step: '01',
    icon: Search,
    title: 'Discovery & Research',
    description: 'We start by understanding your business, goals, target audience, and project requirements through detailed consultations.',
    details: [
      'Initial consultation meeting',
      'Requirement gathering',
      'Market research',
      'Competitor analysis',
      'Goal definition',
    ],
    imageUrl:'https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=800'
  },
  {
    step: '02',
    icon: FileText,
    title: 'Planning & Strategy',
    description: 'Creating a comprehensive project roadmap with clear milestones, timelines, and technical specifications.',
    details: [
      'Project scope definition',
      'Technical architecture',
      'Timeline creation',
      'Resource allocation',
      'Risk assessment',
    ],
    imageUrl:'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=800'
  },
  {
    step: '03',
    icon: Code,
    title: 'Development & Design',
    description: 'We build your solution using proven technologies and best practices.',
    details: [
      'UI/UX design',
      'Frontend development',
      'Backend development',
      'Database setup',
      'API integration',
    ],
    imageUrl:'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?q=80&w=800'
  },
  {
    step: '04',
    icon: TestTube,
    title: 'Testing & Quality Assurance',
    description: 'Rigorous testing ensures your product is bug-free, secure, and performs optimally.',
    details: [
      'Unit testing',
      'Integration testing',
      'Performance testing',
      'Security audit',
      'User acceptance testing',
    ],
    imageUrl:'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=800'
  },
  {
    step: '05',
    icon: Rocket,
    title: 'Launch & Deployment',
    description: 'Smooth deployment to production with minimal downtime and comprehensive go-live support.',
    details: [
      'Server setup',
      'Domain configuration',
      'Production deployment',
      'Performance monitoring',
      'Launch support',
    ],
    imageUrl:'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=800'
  },
  {
    step: '06',
    icon: HeadphonesIcon,
    title: 'Support & Maintenance',
    description: 'Ongoing support, updates, and optimization to keep your solution running smoothly.',
    details: [
      'Responsive technical support',
      'Bug fixes & updates',
      'Performance optimization',
      'Feature enhancements',
      'Security patches',
    ],
    imageUrl:'https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=800'
  },
];

export default function ProcessPage() {
  return (
    <>
      <Helmet>
        <title>Our Process - FuturePath Technologies</title>
        <meta name="description" content="Learn about our proven development process: from discovery to deployment and ongoing support." />
      </Helmet>

      <section className="relative min-h-[40vh] flex items-center justify-center pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-transparent opacity-50" />
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold font-outfit mb-6 text-white" data-testid="process-hero-title">
              How We Work
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Our proven 6-step process ensures project success from start to finish
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding" data-testid="process-steps-section">
        <div className="container-custom">
          <div className="space-y-20">
            {process.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`process-step-${index}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="glass-card p-10 rounded-3xl">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                        <item.icon size={32} className="text-white" />
                      </div>
                      <div className="text-5xl font-bold font-outfit text-primary/30">
                        {item.step}
                      </div>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold font-outfit mb-4 text-white">
                      {item.title}
                    </h2>
                    <p className="text-muted-foreground text-lg mb-6">
                      {item.description}
                    </p>
                    <ul className="space-y-3">
                      {item.details.map((detail, i) => (
                        <li key={i} className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full bg-primary" />
                          <span className="text-muted-foreground">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    className="rounded-3xl glass-card p-2 w-full"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface/30" data-testid="cta-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass rounded-3xl p-12 md:p-16 text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold font-outfit mb-6 text-white">
              Ready to Start Your Project?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Let's begin with a free consultation to discuss your requirements.
            </p>
            <Button
              asChild
              size="lg"
              className="rounded-full bg-primary hover:bg-primary/90 neon-glow"
              data-testid="cta-btn"
            >
              <Link to="/consultation">
                Schedule Consultation
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </>
  );
}
